import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from '../../app.config';

export interface PortletSessionRequest {
    policyNumber: string;
    propertyZipCode: string;
    streetNumber?: string;
    phoneNumber?: string;
}

export interface PortletSessionResponse {
    sessionId: string;
}

@Injectable({ providedIn: 'root' })
export class PortletService {
    constructor(private http: HttpClient, @Inject(APP_CONFIG) private config: AppConfig) {}

    /**
     * Calls the auth session endpoint and returns the raw sessionId.
     */
    createSession(request: PortletSessionRequest): Observable<string> {
        const body: PortletSessionRequest = {
            policyNumber: request.policyNumber,
            propertyZipCode: request.propertyZipCode,
            streetNumber: request.streetNumber ?? '',
            phoneNumber: request.phoneNumber ?? '',
        };
        const headers = { 'x-api-key': this.config.authSessionApiKey };

        return this.http.post<PortletSessionResponse>(this.config.authSessionUrl, body, { headers }).pipe(
            map((response) => {
                const sessionId = response?.sessionId;
                if (!sessionId) {
                    throw new Error('Missing sessionId in auth session response.');
                }
                return sessionId;
            }),
            catchError((error) => throwError(() => error))
        );
    }

    /**
     * Builds the full portlet link with sessionId appended as a query param.
     */
    buildPortletLink(sessionId: string): string {
        const base = this.config.portletUrl;
        const separator = base.includes('?') ? '&' : '?';
        return `${base}${separator}sessionId=${encodeURIComponent(sessionId)}`;
    }

    /**
     * Convenience: creates a session and returns the ready-to-open portlet URL.
     */
    getPortletLink(request: PortletSessionRequest): Observable<string> {
        return this.createSession(request).pipe(map((sessionId) => this.buildPortletLink(sessionId)));
    }
}
